import React, {useContext, useEffect, useRef, useState} from "react";
import {Modal, ScrollView, View} from "react-native";
import MainStyle from "../../MainStyle";
import {colors} from "../assets/styles/colors";
import {StoreContext} from "../components/StoreContextProvider";
import Select from "../components/Select";
import icon from "../assets/img/icons/pencil-line 1.svg";
import fs_icon from "../assets/img/icons/pencil-line fs.svg";
import {useNavigation} from "@react-navigation/native";
import Button from "../components/Button";
import Div from "../lib/Div";
import P from "../lib/P";
import {isFullSwing} from "../constants";

type Props = {
  isOpen: boolean, onClose: () => void, joinTournament: (val: string) => void, totalShots?: number, entryFee: number,
  type: 'RegistrationConfirmationModal' | 'LowBalanceModal' | 'ChallengeFeeConfirmationModal' | 'StartAutoPlay' | 'StopAutoPlay' | 'ChallengeEntry' | 'CancelCashChallenge' | 'ChallengeAmountModal' | 'LeaveTournament' | '',
  helperText?: string, entryChallengeFee?: string, autoPlayHandler?: () => void, min_h?: number
};

const TournamentRegistrationConfirmationModal = (props: Props) => {
  const {isOpen, type, totalShots, entryFee, helperText, entryChallengeFee, min_h} = props;
  const [value, setValue] = useState(entryChallengeFee || '0');
  const Context = useContext(StoreContext);
  const {balance, extendedUserData} = Context;
  const [items, setItems] = useState([{value: '-2', customLabel: '', label: 'Max'}]);
  const navigation: any = useNavigation();
  const showDropDown = type === 'RegistrationConfirmationModal' || type === 'ChallengeFeeConfirmationModal' || type === 'StartAutoPlay' || type === 'CancelCashChallenge' || type === 'ChallengeAmountModal';
  const [disableItems, setDisableItems] = useState([]);
  // Tracks whether the user has explicitly picked a dropdown value during the
  // current open, so the options effect below never overwrites their choice on
  // a background balance / maxShotAmount refresh.
  const userSelectedRef = useRef(false);

  const handlePress = () => {
    if (type === 'LowBalanceModal') {
      props.onClose();
      navigation.navigate({name: 'account', params: {step: 1}});

    } else if (type === 'StopAutoPlay' && props.autoPlayHandler) {
      props.autoPlayHandler();

    } else {
      props.joinTournament(value);
    }
  };

  // Re-arm the per-type default each time the modal (re)opens or its type
  // changes, so a fresh open starts from that type's default selection.
  useEffect(() => {
    userSelectedRef.current = false;
  }, [type, isOpen]);

  useEffect(() => {
    let val = totalShots ? [{value: '0', customLabel: '', label: '0'}] : [{value: '-2', customLabel: '', label: 'Max'}];
    const disableList: any = [];
    const availableBalance = balance - entryFee;
    const shotOptions = [3, 5, 10, 15, 20];

    const isNewPlayerChallengeFee = !totalShots && type === 'ChallengeFeeConfirmationModal' && (extendedUserData.isNewPlayer || extendedUserData.freeShotNumber !== extendedUserData.freeShotCount);

    if (isNewPlayerChallengeFee) {
      val = [{value: '3', customLabel: '', label: '$3'}];

    } else if (type === 'StartAutoPlay' || type === 'ChallengeAmountModal') {
      shotOptions.reverse().forEach(shotOption => {
        if (shotOption * 100 <= extendedUserData.maxShotAmount) {
          val.push({value: `${shotOption}`, customLabel: '', label: `$${shotOption}`});

        } else {
          disableList.push({value: `${shotOption}`, label: `$${shotOption}`});
        }
      });

    } else {
      shotOptions.forEach(shotOption => {
        const totalCost = (shotOption * 100) * (totalShots || 1);
        if (totalCost <= availableBalance && shotOption * 100 <= extendedUserData.maxShotAmount) {
          val.push({
            value: `${shotOption}`,
            customLabel: `$${shotOption}`,
            label: `$${shotOption} (Total: $${totalCost / 100})`
          });
        }
      });
    }

    setDisableItems(disableList);
    setItems(val);

    // Default the dropdown selection per modal type — but only until the user
    // has made a choice (userSelectedRef). This effect re-fires on every
    // balance / maxShotAmount refresh to rebuild the option list; without the
    // guard those refreshes would silently overwrite the user's pick. A
    // tournament (RegistrationConfirmationModal / ChallengeAmountModal) must
    // default to $0 — it previously defaulted to the max affordable option /
    // the '-2' tier-max sentinel, so a "free" tournament sent a $10 challenge
    // fee that the server then pinned onto every zero-fee tournament bet
    // (SMG-3600 was the server-side half of this; the client must send 0).
    if (!userSelectedRef.current) {
      if (isNewPlayerChallengeFee) setValue('3');
      else if (type === 'StartAutoPlay') setValue('-2');
      else if (type === 'ChallengeAmountModal' || type === 'CancelCashChallenge' || type === 'RegistrationConfirmationModal') setValue('0');
    }
  }, [balance, entryFee, totalShots, extendedUserData.maxShotAmount, type]);

  return (
    <Modal animationType="fade" transparent visible={isOpen} statusBarTranslucent={false} hardwareAccelerated={false}
           onRequestClose={props.onClose}>
      <View style={[MainStyle.fix_gradient_and_modal_error_in_landscape, MainStyle.flex1, MainStyle.bg_filter_modal]}>
        <ScrollView contentContainerStyle={MainStyle.flex1}>
          <Div minH="100%" alignItems="center" justifyContent="center">
            <Div minH={min_h} bg={isFullSwing ? '#131314' : colors.settings_bg} maxW={332} w="100%" rounded={20}
                 overflow="hidden" borderColor={colors.settings_border} borderWidth={1} pt={isFullSwing ? 0 : 30}
                 pb={41}>
              <P fw="500" fs={isFullSwing ? 16 : 26} ta="center" bg={isFullSwing ? '#252526' : undefined}
                 lh={isFullSwing ? 48 : undefined}>
                {type === 'LowBalanceModal' && (isFullSwing ? 'Insufficient Funds' : 'Insufficient balance to enter Tournament with existing amount.')}
                {type === 'RegistrationConfirmationModal' && `Confirm Tournament Entry (${entryFee ? `$${entryFee / 100}` : 'Free'})`}
                {(type === 'ChallengeEntry' || type === 'ChallengeAmountModal') && 'Confirm Challenge Entry'}
                {type === 'ChallengeFeeConfirmationModal' && `Choose Your Challenge Fee`}
                {type === 'StartAutoPlay' && `Please Confirm\nStart Auto-Play`}
                {type === 'StopAutoPlay' && `Please Confirm\nCanceling Auto-Play`}
                {type === 'CancelCashChallenge' && `Please Confirm\nCancelling Cash\nChallenges`}
                {type === 'LeaveTournament' && 'Leave Tournament'}
              </P>

              <View style={showDropDown ? [MainStyle.flex1, MainStyle.min_h_32] : MainStyle.h_51}/>

              <Div mx={20}>
                {showDropDown && (
                  <Div row alignItems="center" mb={30}>
                    <P ta="left" fw="500" w={170} tt="uppercase">
                      {type === 'CancelCashChallenge' ? `Challenge Fee\nper Shot:` : `Additional Solo Challenge per Shot:`}
                    </P>

                    <Div flex={1}/>

                    <Select
                      value={value} onChangeText={(val) => { setValue(val); userSelectedRef.current = true; }} customItems={items}
                      pl={17} pr={21} w={111} borderRadius={20} h={63} textAlign="left"
                      icon={isFullSwing ? fs_icon : icon} fontSize={22} borderColor={colors.settings_border}
                      bg={isFullSwing ? '#252526' : colors.loading_login}
                      color={isFullSwing ? '#0AF' : colors.newGreenText} iconSize={24} secondItems={disableItems}
                      disabled={type === 'CancelCashChallenge'}
                      disabledItemsBottom={!(type === 'StartAutoPlay' || type === 'ChallengeAmountModal')}
                      popupTitle={type !== 'StartAutoPlay' ? 'Win Money with Every Shot!' : 'Play more to activate higher challenges!'}
                    />
                  </Div>
                )}

                <Button
                  onPress={handlePress} mb={20}
                  bg={isFullSwing ? '#3369FF' : type === 'LeaveTournament' ? colors.stop_auto_play : ''}
                  text={type === 'LowBalanceModal' ? 'Take me to deposit' : type === 'RegistrationConfirmationModal' ? 'Enter Tournament' : type === 'LeaveTournament' ? 'Leave Tournament' : 'Confirm'}
                  color={isFullSwing ? 'white' : type === 'LeaveTournament' ? colors.newGreenText : ''}
                  radius={isFullSwing ? 4 : undefined}
                />

                <Button
                  onPress={props.onClose} radius={isFullSwing ? 4 : undefined}
                  bg={isFullSwing ? '#252526' : colors.loading_login} borderWidth={1} borderColor={isFullSwing ? '#4C4C4C' : 'white'} color="white"
                  text={type === 'LowBalanceModal' ? 'Back to the Tournament Registration' : (type === 'StartAutoPlay' || type === 'StopAutoPlay') ? 'Back' : 'Cancel'}
                />
              </Div>

              {!!helperText && (
                <Div maxW={389} w="100%" mt={34}>
                  <P ta="center" fw="500" fs={14}>{helperText}</P>
                </Div>
              )}

              {type === 'LeaveTournament' && (
                <Div maxW={389} w="100%" mt={34}>
                  <P ta="center" fw="500" fs={14}>
                    If you leave the tournament, you will not receive a refund for your entry fee.
                  </P>
                </Div>
              )}
            </Div>
          </Div>
        </ScrollView>
      </View>
    </Modal>
  );
};

export default React.memo(TournamentRegistrationConfirmationModal);
